import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { useThemeStore } from '../../store/theme';

interface SearchInputProps {
    onSubmit: (query: string) => void;
    onClose?: () => void;
    placeholder?: string;
    className?: string;
}

const SearchInput: React.FC<SearchInputProps> = ({
    onSubmit,
    onClose,
    placeholder = "Search...",
    className = ""
}) => {
    const { colors, theme } = useThemeStore();
    const [query, setQuery] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isExpanded && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isExpanded]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (query.trim()) {
            onSubmit(query.trim());
            setQuery('');
            setIsExpanded(false);
        }
    };

    const handleClose = () => {
        setQuery('');
        setIsExpanded(false);
        onClose?.();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            handleClose();
        }
    };

    if (!isExpanded) {
        return (
            <button
                onClick={() => setIsExpanded(true)}
                className={`flex items-center space-x-2 p-2 rounded-lg transition-all duration-200 hover:opacity-80 ${className}`}
                style={{
                    backgroundColor: theme === 'light' ? '#f3f4f6' : '#374151',
                    color: colors.gray
                }}
            >
                <Search size={20} />
                <span className="hidden sm:inline text-sm">{placeholder}</span>
            </button>
        );
    }

    return (
        <form onSubmit={handleSubmit} className={`relative ${className}`}>
            <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                    <Search
                        size={20}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2"
                        style={{ color: colors.gray }}
                    />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        className="w-full pl-10 pr-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 transition-all duration-200"
                        style={{
                            backgroundColor: theme === 'light' ? '#f9fafb' : '#374151',
                            borderColor: theme === 'light' ? '#e5e7eb' : '#374151',
                            color: colors.text,
                            border: `1px solid ${theme === 'light' ? '#e5e7eb' : '#374151'}`,
                            focusRingColor: colors.primary
                        }}
                    />
                </div>
                <button
                    type="button"
                    onClick={handleClose}
                    className="p-2 rounded-lg transition-all duration-200 hover:opacity-80"
                    style={{
                        backgroundColor: theme === 'light' ? '#f3f4f6' : '#374151',
                        color: colors.gray
                    }}
                >
                    <X size={20} />
                </button>
            </div>
        </form>
    );
};

export default SearchInput;
